import React, { useEffect, useReducer, useState } from 'react';
import { useConfig } from '../../../DataStore/ConfigContext';
import Notification from '../../Common/Notification';
import CallOut from '../../Common/CallOut';
import TextInput from '../../Common/TextInput';
import ajaxConfig from '../../../util/remote/ajaxConfig';
import { useTheme } from '../../../DataStore/ThemeContext';

/* --- helpers ------------------------------------------------------------- */
const statusToOption = (s) => {
  // accepts 'Active' | 'Inactive' | '1' | '0' | {label,value}
  if (s && typeof s === 'object' && 'label' in s && 'value' in s) return s;
  const norm = String(s ?? '').toLowerCase();
  if (norm === '1' || norm === 'active')   return { label: 'Active', value: '1' };
  if (norm === '0' || norm === 'inactive') return { label: 'Inactive', value: '0' };
  // default
  return { label: 'Active', value: '1' };
};

const buildStateFrom = (signatory) => ({
  first_name:   signatory?.first_name ?? '',
  last_name:    signatory?.last_name ?? '',
  designation:  signatory?.designation ?? signatory?.position ?? '',
  status:       statusToOption(signatory?.status),
  loading:      false,
});

/* --- reducer ------------------------------------------------------------- */
const reducer = (state, action) => {
  switch (action.type) {
    case 'RESET':
      return buildStateFrom(action.payload || {}); // reset to current prop payload if provided
    case 'SET_ALL':
      return { ...state, ...action.value };
    default:
      return { ...state, [action.type]: action.value };
  }
};

const EditSignatory = ({ signatory }) => {
  // initialize from props on first render
  const [state, dispatch] = useReducer(reducer, signatory, buildStateFrom);
  const [notify, setNotify] = useState(null);
  const { getSignatories } = useConfig();
  const { setSystemFunction, closeSettings } = useTheme();

  // keep in sync if `signatory` prop changes later
  useEffect(() => {
    dispatch({ type: 'SET_ALL', value: buildStateFrom(signatory) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatory?.id]);

  useEffect(() => {
    if (!notify) return;
    const t = setTimeout(() => setNotify(null), 4500);
    return () => clearTimeout(t);
  }, [notify]);

  useEffect(() => {
      setSystemFunction(() => handleSave);
      return () => setSystemFunction(() => {});
    }, [state]);

  const onChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: name, value });
  };

  const validate = () => {
    if (!state.first_name.trim()) return 'First name is required.';
    if (!state.last_name.trim()) return 'Last name is required.';
    if (!state.designation.trim()) return 'Designation is required.';
    if (!state.status?.value) return 'Status is required.';
    return null;
  };

  const RenderStatusSelect = () => {
    const options = [
      { label: 'Active', value: '1' },
      { label: 'Inactive', value: '0' },
    ];
    return (
      <TextInput
        label="Status"
        type="select"
        name="status"
        leftIcon="fa fa-flag"
        value={state.status}
        options={options}
        onChange={(item) => dispatch({ type: 'status', value: item })}
        placeholder={state.status?.label || 'Select Status…'}
        wrap
      />
    );
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      setNotify(<Notification type="danger" message={error} />);
      return;
    }

    try {
      dispatch({ type: 'loading', value: true });

      const payload = {
        first_name:  state.first_name.trim(),
        last_name:   state.last_name.trim(),
        designation: state.designation.trim(),
        status:      state.status.value, // '1' | '0'
      };

      // Adjust API as appropriate for update vs create
      const res = await ajaxConfig.update_signatory(signatory?.id, payload.first_name, payload.last_name, payload.designation, payload.status);
      if (res?.status === 'OK') {
        setNotify(<Notification type="success" message={res.message || 'Signatory saved.'} />);
        getSignatories(true);
        closeSettings();
      } else {
        setNotify(<Notification type="danger" message={res?.message || 'Failed to save signatory.'} />);
      }
    } catch (err) {
      setNotify(<Notification type="danger" message={String(err?.message || err)} />);
    } finally {
      dispatch({ type: 'loading', value: false });
    }
  };

  return (
    <>
      <CallOut title="Edit Report Signatory" description="Modify the details of an existing signatory." />
      
      <div className='mt-4'>{notify}</div>

      <form className="form-horizontal" onSubmit={handleSave}>
        <div className="row">
          <div className="col-md-6">
            <TextInput
              type="text"
              label="First Name"
              placeholder="First Name"
              leftIcon="fa fa-user"
              value={state.first_name}
              name="first_name"
              onChange={onChange}
              required
              wrap
            />
          </div>
          <div className="col-md-6">
            <TextInput
              type="text"
              label="Last Name"
              placeholder="Last Name"
              leftIcon="fa fa-user"
              value={state.last_name}
              name="last_name"
              onChange={onChange}
              required
              wrap
            />
          </div>
        </div>

        <TextInput
          type="text"
          label="Designation"
          placeholder="e.g. Executive Director"
          leftIcon="fa fa-id-badge"
          value={state.designation}
          name="designation"
          onChange={onChange}
          required
          wrap
        />

        <RenderStatusSelect />
        <small className="form-text text-muted">Active signatories appear on report signature blocks.</small>

        {/* Preview */}
        <div className="border rounded p-3 mb-3">
          <div className="small text-muted mb-2">Print preview</div>
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle d-inline-flex align-items-center justify-content-center mr-3"
              style={{ width: 48, height: 48, background: '#E9F2FF', color: '#111827', fontWeight: 700 }}
            >
              {`${(state.first_name || 'S').charAt(0)}${(state.last_name || 'N').charAt(0)}`.toUpperCase()}
            </div>
            <div>
              <div className="font-weight-semibold">
                {(state.first_name || '').trim()} {(state.last_name || '').trim()}
              </div>
              <div className="text-muted small" style={{ letterSpacing: '.04em' }}>
                {(state.designation || '').trim() || '—'}
              </div>
              <div className="mt-3" style={{ height: 1, background: '#E5E7EB' }} />
              <div className="d-flex justify-content-between text-muted small pt-1">
                <span>Signature</span>
                <span>Date</span>
              </div>
            </div>
          </div>
        </div>

      </form>
    </>
  );
};

export default EditSignatory;
